import React, { useState, useRef, useEffect, useMemo, KeyboardEvent } from "react"
import { Link, useLocation } from "react-router-dom"
import {
  Box,
  Drawer,
  Flex,
  HStack,
  IconButton,
  Portal,
  Spinner,
  Stack,
  Textarea,
} from "@chakra-ui/react"
import { LuArrowDown, LuArrowUp, LuCheck, LuCopy, LuMessageSquarePlus, LuPaperclip, LuPencil, LuSend, LuSquare, LuTrash2, LuX } from "react-icons/lu"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import {
  ChatAttachment,
  sendChatMessage,
  listConversations,
  createConversation,
  getConversation,
  deleteConversation,
  renameConversation,
  ChatMessage,
  ChatConversationSummary,
  PageContext,
} from "@/api/chat"
import { confirmEmployeeCleaning, confirmEmployeeDuplicates, DuplicateGroupParsed } from "@/api/employees"
import { Button, Text } from "@/components/ui"

const TABLE_STYLE: React.CSSProperties = {
  borderCollapse: "collapse",
  width: "100%",
  margin: "20px 0 32px 0",
  fontSize: "12px",
}

const TH_STYLE: React.CSSProperties = {
  background: "rgba(255,255,255,0.12)",
  fontWeight: 700,
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: "9px 24px",
  textAlign: "left",
  borderBottom: "2px solid rgba(255,255,255,0.3)",
  borderRight: "1px solid rgba(255,255,255,0.22)",
}

const TD_STYLE: React.CSSProperties = {
  padding: "14px 24px",
  verticalAlign: "top",
  lineHeight: "1.55",
  borderBottom: "1px solid rgba(255,255,255,0.12)",
  borderRight: "1px solid rgba(255,255,255,0.18)",
}

const markdownComponents = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
    if (href?.startsWith("/")) {
      return (
        <Link to={href} style={{ color: "#93c5fd", textDecoration: "underline", fontWeight: 600, cursor: "pointer" }}>
          {children}
        </Link>
      )
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "#93c5fd", textDecoration: "underline", fontWeight: 600 }}>
        {children}
      </a>
    )
  },
  table: ({ children }: { children?: React.ReactNode }) => (
    <table style={TABLE_STYLE}>{children}</table>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th style={TH_STYLE}>{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td style={TD_STYLE}>{children}</td>
  ),
  hr: () => (
    <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.22)", margin: "28px 0", display: "block" }} />
  ),
  br: () => <span style={{ display: "block", marginTop: "14px" }} />,
}

const CHAT_MARKDOWN_SX = {
  "& p": { marginBottom: "20px", lineHeight: "1.75" },
  "& ul, & ol": { paddingLeft: "20px", marginBottom: "12px" },
  "& li": { marginBottom: "9px", lineHeight: "1.65" },
  // Content-rich list items (containing paragraphs/breaks) get more spacing
  "& li + li": { marginTop: "16px" },
  "& ol > li": { paddingBottom: "14px", borderBottom: "1px solid rgba(255,255,255,0.07)" },
  "& ol > li:last-child": { borderBottom: "none", paddingBottom: "0" },
  "& strong": { fontWeight: "700" },
  "& code": {
    background: "rgba(255,255,255,0.15)",
    padding: "1px 4px",
    borderRadius: "3px",
    fontFamily: "monospace",
    fontSize: "11px",
  },
  "& h1, & h2, & h3": { fontWeight: "700", marginBottom: "10px", marginTop: "18px" },
  "& h1:first-child, & h2:first-child, & h3:first-child": { marginTop: "0" },
  "& a": { color: "#93c5fd", textDecoration: "underline", cursor: "pointer", fontWeight: 600 },
  "& a:hover": { color: "#bfdbfe", textDecoration: "underline" },
}

const renderMarkdown = (content: string) => (
  <Box className="chat-markdown" sx={CHAT_MARKDOWN_SX}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents as any}>{content}</ReactMarkdown>
  </Box>
)

// ── Bulk tabs parser ─────────────────────────────────────────
// When the assistant wraps results in [bulk_tabs]...[/bulk_tabs] markers,
// we parse them into a tabbed UI. If no markers are found, returns null
// and the message renders as plain markdown (graceful degradation).

interface BulkTab {
  label: string       // Display label: "Brian Yu — Copy.ai"
  content: string
  dealId?: number
  companyId?: number
}

interface BulkTabData {
  before: string
  tabs: BulkTab[]
  after: string
}

function parseTabLabel(raw: string): { label: string; dealId?: number; companyId?: number } {
  const pipeIdx = raw.indexOf("|")
  if (pipeIdx === -1) return { label: raw.trim() }
  const label = raw.slice(0, pipeIdx).trim()
  const meta = raw.slice(pipeIdx + 1).trim()
  const dealMatch = meta.match(/dealId=(\d+)/)
  const companyMatch = meta.match(/companyId=(\d+)/)
  return {
    label,
    dealId: dealMatch ? parseInt(dealMatch[1], 10) : undefined,
    companyId: companyMatch ? parseInt(companyMatch[1], 10) : undefined,
  }
}

function parseBulkTabs(text: string): BulkTabData | null {
  const match = text.match(/\[bulk_tabs\]\s*\n?([\s\S]*?)\[\/bulk_tabs\]/)
  if (!match) return null

  const before = text.slice(0, match.index!).trim()
  const after = text.slice(match.index! + match[0].length).trim()
  const inner = match[1]
  const parts = inner.split(/\[tab:\s*([^\]]+)\]/)
  const tabs: BulkTab[] = []

  // parts alternates: [preamble, label1, content1, label2, content2, ...]
  for (let i = 1; i < parts.length - 1; i += 2) {
    const raw = parts[i].trim()
    const content = (parts[i + 1] || "").trim()
    const { label, dealId, companyId } = parseTabLabel(raw)
    if (label) tabs.push({ label, content, dealId, companyId })
  }

  return tabs.length > 0 ? { before, tabs, after } : null
}

function stripTabMarkers(text: string): string {
  return text
    .replace(/\[bulk_tabs\]\s*\n?/g, "")
    .replace(/\[\/bulk_tabs\]/g, "")
    .replace(/\[tab:\s*([^\]]+)\]/g, "--- $1 ---")
    .trim()
}

function stripCleaningMarkers(text: string): string {
  let result = text.replace(
    /\[cleaning_employees\s+companyId=\d+\]\s*([\s\S]*?)\[\/cleaning_employees\]/g,
    (_: string, rows: string) => {
      const lines = rows.trim().split("\n").map((line: string) => {
        const parts = line.split("|")
        if (parts.length >= 4) {
          return `- ${parts[1].trim()} (${parts[2].trim()}) — ${parts[3].trim().replace(/_/g, " ")}`
        }
        return `- ${line}`
      })
      return lines.join("\n")
    }
  )
  result = result.replace(
    /\[duplicate_employees\s+companyId=\d+\]\s*([\s\S]*?)\[\/duplicate_employees\]/g,
    (_: string, content: string) => {
      const lines = content.trim().split("\n").map((line: string) => {
        const parts = line.split("|")
        if (parts[0].trim() === "GROUP") return `\nDuplicate Group (${parts[2]?.trim()} confidence): ${parts[3]?.trim()}`
        if (parts[0].trim() === "PRIMARY") return `  KEEP: ${parts[2]?.trim()} (${parts[3]?.trim()})`
        if (parts[0].trim() === "DUPE") return `  DUPE: ${parts[2]?.trim()} (${parts[3]?.trim()})`
        return `  ${line}`
      })
      return lines.join("\n")
    }
  )
  result = result.replace(
    /\[pipeline_actions\]\s*\n?([\s\S]*?)\[\/pipeline_actions\]/g,
    (_: string, body: string) => {
      const lines = body.trim().split("\n").map((line: string) => {
        const p = line.split("|")
        if (p.length >= 7) return `${p[0].trim()}. ${p[2].trim()} — ${p[3].trim()} (${p[4].trim()}): ${p[6].trim()}`
        return `- ${line}`
      })
      return "Pipeline Actions:\n" + lines.join("\n")
    }
  )
  return result
}

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(stripCleaningMarkers(stripTabMarkers(text)))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <Box display="flex" justifyContent="flex-end" mt={1}>
      <Box
        as="button"
        onClick={handleCopy}
        display="flex"
        alignItems="center"
        gap="4px"
        color={copied ? "green.300" : "whiteAlpha.400"}
        _hover={{ color: copied ? "green.300" : "whiteAlpha.700" }}
        fontSize="10px"
        py={1}
        transition="color 0.15s"
      >
        {copied ? <LuCheck size={11} /> : <LuCopy size={11} />}
        {copied ? "Copied" : "Copy"}
      </Box>
    </Box>
  )
}

// ── Cleaning employees interactive renderer ──────────────────
// When the assistant includes [cleaning_employees companyId=N]...[/cleaning_employees]
// markers, we render an interactive checkbox list so users can review and confirm
// which employees to exclude.

const CleaningPreviewRenderer = ({ companyId, employees }: {
  companyId: number
  employees: { id: number; name: string; jobTitle: string; reason: string }[]
}) => {
  const [checked, setChecked] = useState<Record<number, boolean>>(() =>
    Object.fromEntries(employees.map(e => [e.id, true]))
  )
  const [isConfirming, setIsConfirming] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [resultCount, setResultCount] = useState(0)

  const toggleEmployee = (id: number) => {
    if (confirmed) return
    setChecked(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const toggleAll = () => {
    if (confirmed) return
    const allChecked = employees.every(e => checked[e.id])
    setChecked(Object.fromEntries(employees.map(e => [e.id, !allChecked])))
  }

  const selectedCount = employees.filter(e => checked[e.id]).length

  const handleConfirm = async () => {
    const selected = employees.filter(e => checked[e.id])
    if (selected.length === 0) return
    setIsConfirming(true)
    try {
      const res = await confirmEmployeeCleaning(companyId, selected)
      setResultCount(res.excludedCount)
      setConfirmed(true)
    } catch (err) {
      console.error("Error confirming cleaning:", err)
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <Box border="1px solid" borderColor="whiteAlpha.200" borderRadius="md" my={3} overflow="hidden">
      {/* Header */}
      <HStack
        px={3}
        py={2}
        bg="whiteAlpha.100"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        justify="space-between"
      >
        <HStack gap={2}>
          <input
            type="checkbox"
            checked={employees.every(e => checked[e.id])}
            onChange={toggleAll}
            disabled={confirmed}
            style={{ cursor: confirmed ? "default" : "pointer", accentColor: "#e53e3e" }}
          />
          <Text fontSize="xs" fontWeight="600" color={confirmed ? "green.300" : "white"}>
            {confirmed
              ? `${resultCount} employee${resultCount !== 1 ? "s" : ""} excluded successfully`
              : `${selectedCount} of ${employees.length} selected for exclusion`}
          </Text>
        </HStack>
        {!confirmed && (
          <Button
            size="xs"
            colorPalette="red"
            onClick={handleConfirm}
            disabled={selectedCount === 0 || isConfirming}
          >
            {isConfirming ? <Spinner size="xs" /> : `Exclude ${selectedCount}`}
          </Button>
        )}
        {confirmed && (
          <Text fontSize="xs" color="green.300" fontWeight="600">Done</Text>
        )}
      </HStack>

      {/* Employee rows */}
      <Box maxH="300px" overflowY="auto" css={{
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.2)", borderRadius: "2px" },
      }}>
        {employees.map(emp => (
          <HStack
            key={emp.id}
            px={3}
            py={1.5}
            borderBottom="1px solid"
            borderColor="whiteAlpha.100"
            _hover={confirmed ? {} : { bg: "whiteAlpha.50" }}
            cursor={confirmed ? "default" : "pointer"}
            onClick={() => toggleEmployee(emp.id)}
            opacity={confirmed && !checked[emp.id] ? 0.4 : 1}
            transition="opacity 0.15s"
          >
            <input
              type="checkbox"
              checked={checked[emp.id]}
              onChange={() => toggleEmployee(emp.id)}
              onClick={(e) => e.stopPropagation()}
              disabled={confirmed}
              style={{ cursor: confirmed ? "default" : "pointer", flexShrink: 0, accentColor: "#e53e3e" }}
            />
            <Box flex={1} minW={0}>
              <Text fontSize="xs" fontWeight="500" color="white" lineClamp={1}>{emp.name}</Text>
              <Text fontSize="10px" color="whiteAlpha.700" lineClamp={1}>{emp.jobTitle}</Text>
            </Box>
            <Text fontSize="10px" color="whiteAlpha.500" flexShrink={0}>
              {emp.reason.replace(/_/g, " ")}
            </Text>
          </HStack>
        ))}
      </Box>
    </Box>
  )
}

// ── Duplicate employees interactive renderer ──────────────────
// When the assistant includes [duplicate_employees companyId=N]...[/duplicate_employees]
// markers, we render grouped duplicate clusters with confidence badges and checkboxes.

function parseDuplicateGroups(raw: string): DuplicateGroupParsed[] {
  const lines = raw.trim().split("\n")
  const groups: DuplicateGroupParsed[] = []
  let currentGroup: DuplicateGroupParsed | null = null

  for (const line of lines) {
    const parts = line.split("|")
    const tag = parts[0].trim()
    if (tag === "GROUP" && parts.length >= 4) {
      if (currentGroup) groups.push(currentGroup)
      currentGroup = {
        groupId: parseInt(parts[1].trim(), 10),
        confidence: parts[2].trim() as "high" | "medium" | "low",
        reason: parts.slice(3).join("|").trim(),
        primary: { id: 0, name: "", jobTitle: "" },
        duplicates: [],
      }
    } else if (tag === "PRIMARY" && parts.length >= 4 && currentGroup) {
      currentGroup.primary = {
        id: parseInt(parts[1].trim(), 10),
        name: parts[2].trim(),
        jobTitle: parts[3].trim(),
      }
    } else if (tag === "DUPE" && parts.length >= 4 && currentGroup) {
      currentGroup.duplicates.push({
        id: parseInt(parts[1].trim(), 10),
        name: parts[2].trim(),
        jobTitle: parts[3].trim(),
      })
    }
  }
  if (currentGroup) groups.push(currentGroup)
  return groups.filter(g => g.primary.id !== 0 && g.duplicates.length > 0)
}

const CONFIDENCE_COLORS: Record<string, { bg: string; text: string; accent: string }> = {
  high: { bg: "rgba(229, 62, 62, 0.15)", text: "#fc8181", accent: "#e53e3e" },
  medium: { bg: "rgba(214, 158, 46, 0.15)", text: "#ecc94b", accent: "#d69e2e" },
  low: { bg: "rgba(160, 174, 192, 0.15)", text: "#a0aec0", accent: "#718096" },
}

const DuplicatePreviewRenderer = ({ companyId, groups }: {
  companyId: number
  groups: DuplicateGroupParsed[]
}) => {
  const [checked, setChecked] = useState<Record<number, boolean>>(() => {
    const initial: Record<number, boolean> = {}
    for (const group of groups) {
      for (const dupe of group.duplicates) {
        initial[dupe.id] = group.confidence === "high"
      }
    }
    return initial
  })
  const [isConfirming, setIsConfirming] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [resultCount, setResultCount] = useState(0)

  const toggleDupe = (id: number) => {
    if (confirmed) return
    setChecked(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const toggleAll = () => {
    if (confirmed) return
    const allDupeIds = groups.flatMap(g => g.duplicates.map(d => d.id))
    const allChecked = allDupeIds.every(id => checked[id])
    setChecked(prev => {
      const next = { ...prev }
      for (const id of allDupeIds) next[id] = !allChecked
      return next
    })
  }

  const allDupeIds = groups.flatMap(g => g.duplicates.map(d => d.id))
  const selectedCount = allDupeIds.filter(id => checked[id]).length
  const totalDupes = allDupeIds.length

  const handleConfirm = async () => {
    const selected: { id: number; name: string; jobTitle: string; groupId: number }[] = []
    for (const group of groups) {
      for (const dupe of group.duplicates) {
        if (checked[dupe.id]) {
          selected.push({ id: dupe.id, name: dupe.name, jobTitle: dupe.jobTitle, groupId: group.groupId })
        }
      }
    }
    if (selected.length === 0) return
    setIsConfirming(true)
    try {
      const res = await confirmEmployeeDuplicates(companyId, selected)
      setResultCount(res.deduplicatedCount)
      setConfirmed(true)
    } catch (err) {
      console.error("Error confirming duplicates:", err)
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <Box border="1px solid" borderColor="whiteAlpha.200" borderRadius="md" my={3} overflow="hidden">
      {/* Header */}
      <HStack
        px={3}
        py={2}
        bg="whiteAlpha.100"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        justify="space-between"
      >
        <HStack gap={2}>
          <input
            type="checkbox"
            checked={allDupeIds.length > 0 && allDupeIds.every(id => checked[id])}
            onChange={toggleAll}
            disabled={confirmed}
            style={{ cursor: confirmed ? "default" : "pointer", accentColor: "#dd6b20" }}
          />
          <Text fontSize="xs" fontWeight="600" color={confirmed ? "green.300" : "white"}>
            {confirmed
              ? `${resultCount} duplicate${resultCount !== 1 ? "s" : ""} marked successfully`
              : `${selectedCount} of ${totalDupes} selected for deduplication`}
          </Text>
        </HStack>
        {!confirmed && (
          <Button
            size="xs"
            colorPalette="orange"
            onClick={handleConfirm}
            disabled={selectedCount === 0 || isConfirming}
          >
            {isConfirming ? <Spinner size="xs" /> : `Mark ${selectedCount} as Duplicate${selectedCount !== 1 ? "s" : ""}`}
          </Button>
        )}
        {confirmed && (
          <Text fontSize="xs" color="green.300" fontWeight="600">Done</Text>
        )}
      </HStack>

      {/* Duplicate groups */}
      <Box maxH="400px" overflowY="auto" css={{
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.2)", borderRadius: "2px" },
      }}>
        {groups.map(group => (
          <Box key={group.groupId} borderBottom="1px solid" borderColor="whiteAlpha.200">
            {/* Group header with confidence badge */}
            <HStack px={3} py={2} bg="whiteAlpha.50" gap={2}>
              <Box
                px={2}
                py={0.5}
                borderRadius="sm"
                fontSize="10px"
                fontWeight="700"
                letterSpacing="0.5px"
                bg={CONFIDENCE_COLORS[group.confidence]?.bg ?? CONFIDENCE_COLORS.low.bg}
                color={CONFIDENCE_COLORS[group.confidence]?.text ?? CONFIDENCE_COLORS.low.text}
                flexShrink={0}
              >
                {group.confidence.toUpperCase()}
              </Box>
              <Text fontSize="11px" color="whiteAlpha.700" lineClamp={1}>{group.reason}</Text>
            </HStack>

            {/* Primary record (KEEP) */}
            <HStack
              px={3}
              py={1.5}
              bg="rgba(72, 187, 120, 0.06)"
              borderBottom="1px solid"
              borderColor="whiteAlpha.100"
            >
              <Box w="20px" flexShrink={0} />
              <Text fontSize="10px" color="green.300" fontWeight="700" flexShrink={0} w="36px">KEEP</Text>
              <Box flex={1} minW={0}>
                <Text fontSize="xs" fontWeight="500" color="white" lineClamp={1}>{group.primary.name}</Text>
              </Box>
              <Text fontSize="10px" color="whiteAlpha.600" flexShrink={0} lineClamp={1} maxW="180px">{group.primary.jobTitle}</Text>
            </HStack>

            {/* Duplicate records (checkboxes) */}
            {group.duplicates.map(dupe => (
              <HStack
                key={dupe.id}
                px={3}
                py={1.5}
                borderBottom="1px solid"
                borderColor="whiteAlpha.100"
                _hover={confirmed ? {} : { bg: "whiteAlpha.50" }}
                cursor={confirmed ? "default" : "pointer"}
                onClick={() => toggleDupe(dupe.id)}
                opacity={confirmed && !checked[dupe.id] ? 0.4 : 1}
                transition="opacity 0.15s"
              >
                <input
                  type="checkbox"
                  checked={checked[dupe.id]}
                  onChange={() => toggleDupe(dupe.id)}
                  onClick={(e) => e.stopPropagation()}
                  disabled={confirmed}
                  style={{ cursor: confirmed ? "default" : "pointer", flexShrink: 0, accentColor: CONFIDENCE_COLORS[group.confidence]?.accent ?? "#718096" }}
                />
                <Text fontSize="10px" color="orange.300" fontWeight="700" flexShrink={0} w="36px">DUPE</Text>
                <Box flex={1} minW={0}>
                  <Text fontSize="xs" fontWeight="500" color="white" lineClamp={1}>{dupe.name}</Text>
                </Box>
                <Text fontSize="10px" color="whiteAlpha.600" flexShrink={0} lineClamp={1} maxW="180px">{dupe.jobTitle}</Text>
              </HStack>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

// ─── Pipeline Actions Renderer ─────────────────────────────────────────────────
interface PipelineAction {
  num: number
  dealId: number
  clientName: string
  companyName: string
  stage: string
  action: string
  actionLabel: string
}

const STANDARD_ACTIONS: { action: string; label: string }[] = [
  { action: "send_intro_email", label: "Send intro email" },
  { action: "advance_nda_signed", label: "Advance to NDA Signed" },
  { action: "advance_ready_for_offer", label: "Advance to Ready for Offer" },
  { action: "scan_grants", label: "Scan grants from vesting files" },
  { action: "advance_and_scan", label: "Advance to Ready for Offer + scan grants" },
  { action: "other", label: "Other..." },
]

function parsePipelineActions(body: string): PipelineAction[] {
  return body.trim().split("\n")
    .map(line => {
      const p = line.split("|")
      if (p.length >= 7) {
        return {
          num: parseInt(p[0].trim(), 10),
          dealId: parseInt(p[1].trim(), 10),
          clientName: p[2].trim(),
          companyName: p[3].trim(),
          stage: p[4].trim(),
          action: p[5].trim(),
          actionLabel: p[6].trim(),
        }
      }
      return null
    })
    .filter((r): r is PipelineAction => r !== null && !isNaN(r.dealId))
}

const PipelineActionsRenderer = ({ actions, onExecute }: {
  actions: PipelineAction[]
  onExecute: (text: string) => void
}) => {
  const [checked, setChecked] = useState<Record<number, boolean>>(() =>
    Object.fromEntries(actions.map(a => [a.dealId, true]))
  )
  const [sent, setSent] = useState(false)
  const [overrides, setOverrides] = useState<Record<number, string>>({})
  const [customText, setCustomText] = useState<Record<number, string>>({})

  const getEffectiveLabel = (a: PipelineAction): string => {
    const override = overrides[a.dealId]
    if (!override) return a.actionLabel
    if (override === "other") return customText[a.dealId]?.trim() || ""
    const found = STANDARD_ACTIONS.find(s => s.action === override)
    return found ? found.label : a.actionLabel
  }

  const toggleAction = (dealId: number) => {
    if (sent) return
    setChecked(prev => ({ ...prev, [dealId]: !prev[dealId] }))
  }

  const toggleAll = () => {
    if (sent) return
    const allChecked = actions.every(a => checked[a.dealId])
    setChecked(Object.fromEntries(actions.map(a => [a.dealId, !allChecked])))
  }

  const selectedCount = actions.filter(a => checked[a.dealId]).length

  const hasInvalidOther = actions
    .filter(a => checked[a.dealId])
    .some(a => overrides[a.dealId] === "other" && !customText[a.dealId]?.trim())

  const handleExecute = () => {
    const selected = actions.filter(a => checked[a.dealId])
    if (selected.length === 0) return
    const lines = selected.map((a, i) => {
      const label = getEffectiveLabel(a)
      return `${i + 1}. Deal ${a.dealId} (${a.clientName} — ${a.companyName}): ${label}`
    })
    const text = `Execute the following pipeline actions:\n${lines.join("\n")}`
    setSent(true)
    onExecute(text)
  }

  return (
    <Box border="1px solid" borderColor="whiteAlpha.200" borderRadius="md" my={3} overflow="hidden">
      {/* Header */}
      <HStack
        px={3}
        py={2}
        bg="whiteAlpha.100"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        justify="space-between"
      >
        <HStack gap={2}>
          <input
            type="checkbox"
            checked={actions.every(a => checked[a.dealId])}
            onChange={toggleAll}
            disabled={sent}
            style={{ cursor: sent ? "default" : "pointer", accentColor: "#3b82f6" }}
          />
          <Text fontSize="xs" fontWeight="600" color={sent ? "green.300" : "white"}>
            {sent
              ? `${selectedCount} action${selectedCount !== 1 ? "s" : ""} sent for execution`
              : `${selectedCount} of ${actions.length} selected`}
          </Text>
        </HStack>
        {!sent && (
          <Button
            size="xs"
            colorPalette="blue"
            onClick={handleExecute}
            disabled={selectedCount === 0 || hasInvalidOther}
          >
            {`Execute ${selectedCount} Action${selectedCount !== 1 ? "s" : ""}`}
          </Button>
        )}
        {sent && (
          <Text fontSize="xs" color="green.300" fontWeight="600">Sent</Text>
        )}
      </HStack>

      {/* Action rows */}
      <Box maxH="400px" overflowY="auto" css={{
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.2)", borderRadius: "2px" },
      }}>
        {actions.map(a => (
          <HStack
            key={a.dealId}
            px={3}
            py={1.5}
            borderBottom="1px solid"
            borderColor="whiteAlpha.100"
            _hover={sent ? {} : { bg: "whiteAlpha.50" }}
            cursor={sent ? "default" : "pointer"}
            onClick={() => toggleAction(a.dealId)}
            opacity={sent && !checked[a.dealId] ? 0.4 : 1}
            transition="opacity 0.15s"
            gap={3}
          >
            <input
              type="checkbox"
              checked={checked[a.dealId]}
              onChange={() => toggleAction(a.dealId)}
              onClick={(e) => e.stopPropagation()}
              disabled={sent}
              style={{ cursor: sent ? "default" : "pointer", flexShrink: 0, accentColor: "#3b82f6" }}
            />
            <Text fontSize="xs" color="whiteAlpha.500" fontWeight="500" flexShrink={0} w="18px" textAlign="right">
              {a.num}
            </Text>
            <Box flex={1} minW={0}>
              <Text fontSize="xs" fontWeight="500" color="white" lineClamp={1}>
                {a.clientName} — {a.companyName}
              </Text>
              {sent ? (
                <Text fontSize="10px" color="whiteAlpha.700" lineClamp={1}>
                  {getEffectiveLabel(a) || a.actionLabel}
                </Text>
              ) : (
                <>
                  <select
                    value={overrides[a.dealId] ?? a.action}
                    onChange={(e) => {
                      e.stopPropagation()
                      setOverrides(prev => ({ ...prev, [a.dealId]: e.target.value }))
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      fontSize: "10px",
                      background: "transparent",
                      color: overrides[a.dealId] && overrides[a.dealId] !== a.action
                        ? "rgba(96,165,250,0.9)"
                        : "rgba(255,255,255,0.7)",
                      border: "none",
                      borderBottom: "1px solid rgba(255,255,255,0.15)",
                      outline: "none",
                      cursor: "pointer",
                      padding: "1px 0",
                      width: "100%",
                      maxWidth: "280px",
                    }}
                  >
                    {!STANDARD_ACTIONS.some(s => s.action === a.action) && (
                      <option value={a.action} style={{ color: "#1a1a1a", background: "#fff" }}>{a.actionLabel}</option>
                    )}
                    {STANDARD_ACTIONS.map(s => (
                      <option key={s.action} value={s.action} style={{ color: "#1a1a1a", background: "#fff" }}>{s.label}</option>
                    ))}
                  </select>
                  {(overrides[a.dealId] === "other") && (
                    <input
                      type="text"
                      placeholder="Describe the action..."
                      value={customText[a.dealId] || ""}
                      onChange={(e) => {
                        e.stopPropagation()
                        setCustomText(prev => ({ ...prev, [a.dealId]: e.target.value }))
                      }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      style={{
                        fontSize: "10px",
                        background: "rgba(255,255,255,0.08)",
                        color: "rgba(255,255,255,0.85)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: "3px",
                        outline: "none",
                        padding: "2px 4px",
                        marginTop: "2px",
                        width: "100%",
                        maxWidth: "280px",
                      }}
                    />
                  )}
                </>
              )}
            </Box>
            <Text fontSize="10px" color="whiteAlpha.500" flexShrink={0} whiteSpace="nowrap">
              {a.stage}
            </Text>
          </HStack>
        ))}
      </Box>
    </Box>
  )
}

// ── Robo Reject renderer ────────────────────────────────────────────────────

interface RoboReject {
  num: number
  dealId: number
  clientName: string
  companyName: string
  stage: string
  daysStale: number
}

function parseRoboRejects(body: string): RoboReject[] {
  return body.trim().split("\n")
    .map(line => {
      const p = line.split("|")
      if (p.length >= 6) {
        return {
          num: parseInt(p[0].trim(), 10),
          dealId: parseInt(p[1].trim(), 10),
          clientName: p[2].trim(),
          companyName: p[3].trim(),
          stage: p[4].trim(),
          daysStale: parseInt(p[5].trim(), 10),
        }
      }
      return null
    })
    .filter((r): r is RoboReject => r !== null && !isNaN(r.dealId))
}

const RoboRejectRenderer = ({ rejects, onExecute }: {
  rejects: RoboReject[]
  onExecute: (text: string) => void
}) => {
  const [checked, setChecked] = useState<Record<number, boolean>>(() =>
    Object.fromEntries(rejects.map(r => [r.dealId, true]))
  )
  const [sent, setSent] = useState(false)

  const toggleReject = (dealId: number) => {
    if (sent) return
    setChecked(prev => ({ ...prev, [dealId]: !prev[dealId] }))
  }

  const toggleAll = () => {
    if (sent) return
    const allChecked = rejects.every(r => checked[r.dealId])
    setChecked(Object.fromEntries(rejects.map(r => [r.dealId, !allChecked])))
  }

  const selectedCount = rejects.filter(r => checked[r.dealId]).length

  const handleExecute = () => {
    const selected = rejects.filter(r => checked[r.dealId])
    if (selected.length === 0) return
    const lines = selected.map((r, i) =>
      `${i + 1}. Deal ${r.dealId} (${r.clientName} — ${r.companyName}): ${r.daysStale} days stale`
    )
    const text = `Reject the following stale deals (move to ESO Passed):\n${lines.join("\n")}`
    setSent(true)
    onExecute(text)
  }

  return (
    <Box border="1px solid" borderColor="whiteAlpha.200" borderRadius="md" my={3} overflow="hidden">
      {/* Header */}
      <HStack
        px={3}
        py={2}
        bg="rgba(239,68,68,0.1)"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        justify="space-between"
      >
        <HStack gap={2}>
          <input
            type="checkbox"
            checked={rejects.every(r => checked[r.dealId])}
            onChange={toggleAll}
            disabled={sent}
            style={{ cursor: sent ? "default" : "pointer", accentColor: "#ef4444" }}
          />
          <Text fontSize="xs" fontWeight="600" color={sent ? "green.300" : "white"}>
            {sent
              ? `${selectedCount} deal${selectedCount !== 1 ? "s" : ""} sent for rejection`
              : `Stale Deals — ${selectedCount} of ${rejects.length} selected`}
          </Text>
        </HStack>
        {!sent && (
          <Button
            size="xs"
            colorPalette="red"
            onClick={handleExecute}
            disabled={selectedCount === 0}
          >
            {`Reject ${selectedCount} Deal${selectedCount !== 1 ? "s" : ""}`}
          </Button>
        )}
        {sent && (
          <Text fontSize="xs" color="green.300" fontWeight="600">Sent</Text>
        )}
      </HStack>

      {/* Reject rows */}
      <Box maxH="400px" overflowY="auto" css={{
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.2)", borderRadius: "2px" },
      }}>
        {rejects.map(r => (
          <HStack
            key={r.dealId}
            px={3}
            py={1.5}
            borderBottom="1px solid"
            borderColor="whiteAlpha.100"
            _hover={sent ? {} : { bg: "whiteAlpha.50" }}
            cursor={sent ? "default" : "pointer"}
            onClick={() => toggleReject(r.dealId)}
            opacity={sent && !checked[r.dealId] ? 0.4 : 1}
            transition="opacity 0.15s"
            gap={3}
          >
            <input
              type="checkbox"
              checked={checked[r.dealId]}
              onChange={() => toggleReject(r.dealId)}
              onClick={(e) => e.stopPropagation()}
              disabled={sent}
              style={{ cursor: sent ? "default" : "pointer", flexShrink: 0, accentColor: "#ef4444" }}
            />
            <Text fontSize="xs" color="whiteAlpha.500" fontWeight="500" flexShrink={0} w="18px" textAlign="right">
              {r.num}
            </Text>
            <Box flex={1} minW={0}>
              <Text fontSize="xs" fontWeight="500" color="white" lineClamp={1}>
                {r.clientName} — {r.companyName}
              </Text>
              <Text fontSize="10px" color="whiteAlpha.500" lineClamp={1}>
                {r.stage}
              </Text>
            </Box>
            <Box bg="rgba(239,68,68,0.15)" px={1.5} py={0.5} borderRadius="full" flexShrink={0}>
              <Text fontSize="10px" color="red.400" fontWeight="600" whiteSpace="nowrap">
                {r.daysStale}d stale
              </Text>
            </Box>
          </HStack>
        ))}
      </Box>
    </Box>
  )
}

// Renders assistant message content, handling [cleaning_employees], [duplicate_employees],
// [pipeline_actions], and [robo_rejects] markers as interactive components and everything else as markdown.
function renderAssistantContent(content: string, onExecute?: (text: string) => void): React.ReactNode {
  const cleaned = stripTabMarkers(content)

  // Regex for cleaning/duplicate markers
  const markerRegex = /\[(cleaning_employees|duplicate_employees)\s+companyId=(\d+)\]\s*([\s\S]*?)\[\/\1\]/g
  // Regex for pipeline actions marker (no companyId)
  const pipelineRegex = /\[pipeline_actions\]\s*\n?([\s\S]*?)\[\/pipeline_actions\]/g
  // Regex for robo rejects marker
  const roboRegex = /\[robo_rejects\]\s*\n?([\s\S]*?)\[\/robo_rejects\]/g

  const hasMarkers = markerRegex.test(cleaned)
  markerRegex.lastIndex = 0
  const hasPipeline = pipelineRegex.test(cleaned)
  pipelineRegex.lastIndex = 0
  const hasRobo = roboRegex.test(cleaned)
  roboRegex.lastIndex = 0

  if (!hasMarkers && !hasPipeline && !hasRobo) {
    return renderMarkdown(cleaned)
  }

  // Combined regex to match all marker types in order
  const allRegex = /\[(cleaning_employees|duplicate_employees)\s+companyId=(\d+)\]\s*([\s\S]*?)\[\/\1\]|\[pipeline_actions\]\s*\n?([\s\S]*?)\[\/pipeline_actions\]|\[robo_rejects\]\s*\n?([\s\S]*?)\[\/robo_rejects\]/g
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let partKey = 0

  while ((match = allRegex.exec(cleaned)) !== null) {
    const beforeText = cleaned.slice(lastIndex, match.index).trim()
    if (beforeText) {
      parts.push(<Box key={`md-${partKey++}`}>{renderMarkdown(beforeText)}</Box>)
    }

    if (match[5] !== undefined) {
      // Robo rejects match (capture group 5)
      const rejects = parseRoboRejects(match[5])
      if (rejects.length > 0 && onExecute) {
        parts.push(<RoboRejectRenderer key={`robo-${partKey++}`} rejects={rejects} onExecute={onExecute} />)
      }
    } else if (match[4] !== undefined) {
      // Pipeline actions match (capture group 4)
      const actions = parsePipelineActions(match[4])
      if (actions.length > 0 && onExecute) {
        parts.push(<PipelineActionsRenderer key={`pipeline-${partKey++}`} actions={actions} onExecute={onExecute} />)
      } else if (actions.length > 0) {
        // No onExecute callback — render as plain text fallback
        parts.push(<Box key={`md-${partKey++}`}>{renderMarkdown(match[0])}</Box>)
      }
    } else {
      // Cleaning/duplicate match (capture groups 1-3)
      const markerType = match[1]
      const companyId = parseInt(match[2], 10)
      const body = match[3]

      if (markerType === "cleaning_employees") {
        const rows = body.trim().split("\n")
          .map(line => {
            const p = line.split("|")
            if (p.length >= 4) {
              return { id: parseInt(p[0].trim(), 10), name: p[1].trim(), jobTitle: p[2].trim(), reason: p[3].trim() }
            }
            return null
          })
          .filter((r): r is { id: number; name: string; jobTitle: string; reason: string } => r !== null)

        if (rows.length > 0) {
          parts.push(<CleaningPreviewRenderer key={`clean-${companyId}-${partKey++}`} companyId={companyId} employees={rows} />)
        }
      } else if (markerType === "duplicate_employees") {
        const groups = parseDuplicateGroups(body)
        if (groups.length > 0) {
          parts.push(<DuplicatePreviewRenderer key={`dupe-${companyId}-${partKey++}`} companyId={companyId} groups={groups} />)
        }
      }
    }

    lastIndex = match.index + match[0].length
  }

  const afterText = cleaned.slice(lastIndex).trim()
  if (afterText) {
    parts.push(<Box key={`md-${partKey++}`}>{renderMarkdown(afterText)}</Box>)
  }

  return <>{parts}</>
}

interface ContextTab {
  id: string                 // "deal-22792"
  label: string              // "Brian Yu — Copy.ai"
  context: PageContext        // { type: "deal", id: 22792 }
  messages: ChatMessage[]     // independent conversation thread
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString()
}

interface ConversationItemProps {
  conv: ChatConversationSummary
  isActive: boolean
  onClick: () => void
  onDelete: () => void
  onRename: (title: string) => void
}

const ConversationItem = ({ conv, isActive, onClick, onDelete, onRename }: ConversationItemProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(conv.title)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing) inputRef.current?.focus()
  }, [isEditing])

  const handleRenameCommit = () => {
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== conv.title) onRename(trimmed)
    setIsEditing(false)
  }

  const lastMsg = conv.messages[0]
  const preview = lastMsg ? lastMsg.content.slice(0, 55) + (lastMsg.content.length > 55 ? "…" : "") : ""

  return (
    <Box
      px={3}
      py={2}
      cursor="pointer"
      bg={isActive ? "hover.bg" : "transparent"}
      _hover={{ bg: "hover.bg" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => { if (!isEditing) onClick() }}
      position="relative"
    >
      {isEditing ? (
        <input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleRenameCommit}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleRenameCommit()
            if (e.key === "Escape") { setEditValue(conv.title); setIsEditing(false) }
            e.stopPropagation()
          }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            background: "transparent",
            border: "1px solid currentColor",
            borderRadius: "4px",
            color: "inherit",
            fontSize: "12px",
            padding: "2px 6px",
            outline: "none",
          }}
        />
      ) : (
        <HStack justify="space-between" align="flex-start" gap={1}>
          <Box flex={1} minW={0}>
            <Text
              fontSize="xs"
              fontWeight={isActive ? "600" : "400"}
              color="text.primary"
              lineClamp={1}
              onDoubleClick={(e) => { e.stopPropagation(); setIsEditing(true) }}
            >
              {conv.title}
            </Text>
            {preview && (
              <Text fontSize="10px" color="text.secondary" lineClamp={1} mt="1px">
                {preview}
              </Text>
            )}
            <Text fontSize="10px" color="text.muted" mt="1px">
              {timeAgo(conv.updatedAt)}
            </Text>
          </Box>
          {isHovered && (
            <HStack gap={0} flexShrink={0}>
              <IconButton
                aria-label="Rename"
                size="xs"
                variant="ghost"
                color="text.secondary"
                _hover={{ color: "text.primary" }}
                onClick={(e) => { e.stopPropagation(); setIsEditing(true) }}
              >
                <LuPencil size={11} />
              </IconButton>
              <IconButton
                aria-label="Delete conversation"
                size="xs"
                variant="ghost"
                color="text.secondary"
                _hover={{ color: "red.400" }}
                onClick={(e) => { e.stopPropagation(); onDelete() }}
              >
                <LuTrash2 size={11} />
              </IconButton>
            </HStack>
          )}
        </HStack>
      )}
    </Box>
  )
}

interface Props {
  isOpen: boolean
  onClose: () => void
}

export const ChatPanel = ({ isOpen, onClose }: Props) => {
  const location = useLocation()
  const pageContext = useMemo<PageContext | null>(() => {
    const parts = location.pathname.split("/")
    // /core/deal/:dealId  or  /core/company/:companyId
    if (parts[1] === "core" && parts[2] === "deal" && parts[3]) {
      const id = parseInt(parts[3], 10)
      return isNaN(id) ? null : { type: "deal", id }
    }
    if (parts[1] === "core" && parts[2] === "company" && parts[3]) {
      const id = parseInt(parts[3], 10)
      return isNaN(id) ? null : { type: "company", id }
    }
    return null
  }, [location.pathname])

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [statusText, setStatusText] = useState("")
  const [streamingText, setStreamingText] = useState("")
  const [conversations, setConversations] = useState<ChatConversationSummary[]>([])
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null)
  const [isLoadingConv, setIsLoadingConv] = useState(false)
  const [attachment, setAttachment] = useState<ChatAttachment | null>(null)
  const [contextTabs, setContextTabs] = useState<ContextTab[]>([])
  const [activeTabId, setActiveTabId] = useState<string | null>(null) // null = Main tab
  const bottomRef = useRef<HTMLDivElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const messageAttachmentNames = useRef<Map<number, string>>(new Map())
  const executePipelineRef = useRef<string | null>(null)

  const activeMessages = useMemo(() => {
    if (!activeTabId) return messages
    return contextTabs.find(t => t.id === activeTabId)?.messages ?? []
  }, [activeTabId, messages, contextTabs])

  const effectiveContext = useMemo<PageContext | null>(() => {
    if (!activeTabId) return pageContext
    return contextTabs.find(t => t.id === activeTabId)?.context ?? pageContext
  }, [activeTabId, contextTabs, pageContext])

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight
    }
  }

  const scrollToTop = () => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = 0
    }
  }

  // Auto-scroll to bottom when messages change or loading state changes
  useEffect(() => {
    scrollToBottom()
  }, [activeMessages, isLoading])

  // Scroll to bottom when the panel opens (after drawer animation settles)
  useEffect(() => {
    if (isOpen && activeMessages.length > 0) {
      setTimeout(scrollToBottom, 150)
    }
  }, [isOpen])

  const loadConversations = async () => {
    try {
      const data = await listConversations()
      setConversations(data)
    } catch {
      // non-fatal
    }
  }

  useEffect(() => {
    if (isOpen) loadConversations()
  }, [isOpen])

  const handleSelectConversation = async (conv: ChatConversationSummary) => {
    if (conv.id === activeConversationId) return
    setIsLoadingConv(true)
    setContextTabs([])
    setActiveTabId(null)
    try {
      const detail = await getConversation(conv.id)
      setMessages(detail.messages)
      setActiveConversationId(conv.id)

      // Reconstruct context tabs from any bulk_tabs markers in saved messages
      for (const msg of detail.messages) {
        if (msg.role === "assistant" && msg.content.includes("[bulk_tabs]")) {
          const parsed = parseBulkTabs(msg.content)
          if (parsed) createContextTabsFromBulkResult(parsed)
          break // only one bulk result per conversation
        }
      }
    } catch {
      // non-fatal
    } finally {
      setIsLoadingConv(false)
    }
  }

  const handleNewChat = () => {
    setMessages([])
    setActiveConversationId(null)
    setInput("")
    setContextTabs([])
    setActiveTabId(null)
  }

  const handleDeleteConversation = async (id: number) => {
    try {
      await deleteConversation(id)
      setConversations((prev) => prev.filter((c) => c.id !== id))
      if (activeConversationId === id) {
        setMessages([])
        setActiveConversationId(null)
      }
    } catch {
      // non-fatal
    }
  }

  const handleRenameConversation = async (id: number, title: string) => {
    try {
      await renameConversation(id, title)
      setConversations((prev) => prev.map((c) => c.id === id ? { ...c, title } : c))
    } catch {
      // non-fatal
    }
  }

  const handleCloseTab = (tabId: string) => {
    setContextTabs(prev => prev.filter(t => t.id !== tabId))
    if (activeTabId === tabId) setActiveTabId(null)
  }

  const createContextTabsFromBulkResult = (parsed: BulkTabData) => {
    const newTabs = parsed.tabs
      .filter(t => t.dealId || t.companyId)
      .map(t => ({
        id: `${t.dealId ? "deal" : "company"}-${t.dealId ?? t.companyId}`,
        label: t.label,
        context: { type: (t.dealId ? "deal" : "company") as "deal" | "company", id: (t.dealId ?? t.companyId)! },
        messages: [{ role: "assistant" as const, content: t.content }],
      }))
    if (newTabs.length > 0) setContextTabs(newTabs)
  }

  const ALLOWED_MIME_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp", "application/pdf"]
  const MAX_FILE_BYTES = 7 * 1024 * 1024 // 7MB — base64 overhead keeps it under 10MB JSON limit

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      alert("Unsupported file type. Please attach a PDF or image (PNG, JPG, GIF, WebP).")
      e.target.value = ""
      return
    }
    if (file.size > MAX_FILE_BYTES) {
      alert("File is too large. Maximum size is 7MB.")
      e.target.value = ""
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // Strip the "data:<mime>;base64," prefix — Anthropic API expects raw base64
      const base64 = result.split(",")[1]
      setAttachment({ data: base64, mimeType: file.type, fileName: file.name })
    }
    reader.readAsDataURL(file)
    e.target.value = "" // Reset so same file can be reselected
  }

  const handleRemoveAttachment = () => setAttachment(null)

  const handleSend = async () => {
    const trimmed = input.trim()
    if (!trimmed || isLoading) return

    const userMessage: ChatMessage = { role: "user", content: trimmed }
    const currentAttachment = attachment
    const isContextTab = activeTabId !== null
    const currentTab = isContextTab ? contextTabs.find(t => t.id === activeTabId) : null

    // Build messages array — from context tab or main conversation
    const updatedMessages = isContextTab && currentTab
      ? [...currentTab.messages, userMessage]
      : [...messages, userMessage]

    // Update the appropriate message store
    if (isContextTab && currentTab) {
      setContextTabs(prev => prev.map(t =>
        t.id === activeTabId ? { ...t, messages: updatedMessages } : t
      ))
    } else {
      setMessages(updatedMessages)
    }

    setInput("")
    setAttachment(null)
    setIsLoading(true)
    setStatusText("Thinking...")
    setStreamingText("")

    // Track attachment filename for display in message history
    if (currentAttachment && !isContextTab) {
      messageAttachmentNames.current.set(updatedMessages.length - 1, currentAttachment.fileName)
    }

    const controller = new AbortController()
    abortControllerRef.current = controller

    // Create conversation on first message if none active (main tab only)
    let convId = activeConversationId
    if (!convId && !isContextTab) {
      try {
        const title = trimmed.slice(0, 60)
        const created = await createConversation(title)
        convId = created.id
        setActiveConversationId(convId)
      } catch {
        // proceed without saving
      }
    }

    try {
      const result = await sendChatMessage(
        updatedMessages,
        isContextTab ? undefined : (convId ?? undefined), // context tabs are ephemeral
        controller.signal,
        (event) => {
          if (event.type === "status") {
            setStatusText(event.text)
            setStreamingText("") // Clear streamed text so spinner/status shows during tool calls
          } else if (event.type === "token") {
            setStreamingText((prev) => prev + event.text)
          }
        },
        isContextTab && currentTab ? currentTab.context : pageContext,
        currentAttachment
      )

      const assistantMsg: ChatMessage = { role: "assistant", content: result.response }

      if (isContextTab) {
        // Write response to context tab's messages
        setContextTabs(prev => prev.map(t =>
          t.id === activeTabId ? { ...t, messages: [...t.messages, assistantMsg] } : t
        ))
      } else {
        // Write response to main messages
        setMessages((prev) => [...prev, assistantMsg])

        // Check for bulk tabs with entity IDs → create context tabs
        const parsed = parseBulkTabs(result.response)
        if (parsed) createContextTabsFromBulkResult(parsed)

        loadConversations()
      }
    } catch (err: any) {
      if (err?.code === "ERR_CANCELED" || err?.name === "AbortError" || err?.name === "CanceledError") return
      const msg = typeof err?.message === "string" && err.message.length < 200 && err.message !== "Internal server error"
        ? err.message
        : "Sorry, something went wrong. Please try again."

      const errorMsg: ChatMessage = { role: "assistant", content: msg }

      if (isContextTab) {
        setContextTabs(prev => prev.map(t =>
          t.id === activeTabId ? { ...t, messages: [...t.messages, errorMsg] } : t
        ))
      } else {
        setMessages((prev) => [...prev, errorMsg])
      }
    } finally {
      setIsLoading(false)
      setStatusText("")
      setStreamingText("")
      abortControllerRef.current = null
    }
  }

  const handleStop = () => {
    abortControllerRef.current?.abort()
    setIsLoading(false)
    setStatusText("")
    setStreamingText("")
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Auto-send pipeline action messages when executePipelineRef is set
  useEffect(() => {
    if (executePipelineRef.current && input === executePipelineRef.current && !isLoading) {
      executePipelineRef.current = null
      handleSend()
    }
  }, [input]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={(e) => { if (!e.open) onClose() }}
      placement="end"
      size="full"
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content bg="card.bg" display="flex" flexDirection="column" w="70vw" maxW="70vw">

            {/* Shared header row — one borderBottom spans full width */}
            <HStack
              flexShrink={0}
              borderBottom="1px solid"
              borderColor="border.default"
              gap={0}
              align="stretch"
            >
              {/* Sidebar header: New Chat button */}
              <HStack
                w="220px"
                flexShrink={0}
                borderRight="1px solid"
                borderColor="border.default"
                px={3}
                py={2}
              >
                <Button size="sm" variant="outline" w="100%" onClick={handleNewChat} gap={2}>
                  <LuMessageSquarePlus size={14} />
                  New Chat
                </Button>
              </HStack>

              {/* Main header: title + close */}
              <HStack flex={1} justify="space-between" px={4} py={2}>
                <HStack gap={2} align="center">
                  <Text fontWeight="semibold" fontSize="md" color="text.primary">
                    ESO Assistant
                  </Text>
                  {effectiveContext ? (
                    <Box
                      px={2}
                      py="2px"
                      bg="blue.800"
                      borderRadius="full"
                      border="1px solid"
                      borderColor="blue.600"
                    >
                      <Text fontSize="10px" color="blue.200" fontWeight="500">
                        {effectiveContext.type === "deal" ? "Deal" : "Company"} #{effectiveContext.id} context
                      </Text>
                    </Box>
                  ) : (
                    <Text fontSize="xs" color="text.secondary">
                      Ask anything about deals, companies, or irr data
                    </Text>
                  )}
                </HStack>
                <IconButton
                  aria-label="Close chat"
                  size="sm"
                  variant="ghost"
                  onClick={onClose}
                >
                  <LuX />
                </IconButton>
              </HStack>
            </HStack>

            {/* Context tab bar — appears when bulk results create entity tabs */}
            {contextTabs.length > 0 && (
              <HStack
                flexShrink={0}
                borderBottom="1px solid"
                borderColor="border.default"
                gap={0}
                align="stretch"
              >
                {/* Spacer matching sidebar width */}
                <Box w="220px" flexShrink={0} borderRight="1px solid" borderColor="border.default" />

                {/* Scrollable tab bar */}
                <HStack
                  flex={1}
                  px={2}
                  py={0}
                  gap={0}
                  overflowX="auto"
                  css={{
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": { height: "3px" },
                    "&::-webkit-scrollbar-thumb": { background: "rgba(128,128,128,0.3)", borderRadius: "2px" },
                  }}
                >
                  {/* Main tab */}
                  <Box
                    as="button"
                    onClick={() => setActiveTabId(null)}
                    px={3}
                    py={2}
                    fontSize="xs"
                    fontWeight={activeTabId === null ? "600" : "400"}
                    color={activeTabId === null ? "text.primary" : "text.secondary"}
                    borderBottom="2px solid"
                    borderColor={activeTabId === null ? "blue.400" : "transparent"}
                    bg={activeTabId === null ? "hover.bg" : "transparent"}
                    _hover={{ color: "text.primary", bg: "hover.bg" }}
                    flexShrink={0}
                    transition="all 0.15s"
                    whiteSpace="nowrap"
                  >
                    Main
                  </Box>

                  {/* Entity tabs */}
                  {contextTabs.map(tab => (
                    <HStack
                      key={tab.id}
                      as="button"
                      onClick={() => setActiveTabId(tab.id)}
                      px={3}
                      py={2}
                      gap={2}
                      fontSize="xs"
                      fontWeight={activeTabId === tab.id ? "600" : "400"}
                      color={activeTabId === tab.id ? "text.primary" : "text.secondary"}
                      borderBottom="2px solid"
                      borderColor={activeTabId === tab.id ? "blue.400" : "transparent"}
                      bg={activeTabId === tab.id ? "hover.bg" : "transparent"}
                      _hover={{ color: "text.primary", bg: "hover.bg" }}
                      flexShrink={0}
                      transition="all 0.15s"
                      whiteSpace="nowrap"
                    >
                      <Box
                        w="6px"
                        h="6px"
                        borderRadius="full"
                        bg={tab.context.type === "deal" ? "blue.400" : "green.400"}
                        flexShrink={0}
                      />
                      <Box as="span">{tab.label}</Box>
                      <Box
                        as="span"
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleCloseTab(tab.id) }}
                        color="text.muted"
                        _hover={{ color: "red.300" }}
                        ml={1}
                        lineHeight={1}
                      >
                        <LuX size={10} />
                      </Box>
                    </HStack>
                  ))}
                </HStack>
              </HStack>
            )}

            {/* Body row: sidebar list + main chat */}
            <HStack flex={1} gap={0} align="stretch" overflow="hidden">

              {/* Sidebar conversation list */}
              <Box
                w="220px"
                flexShrink={0}
                borderRight="1px solid"
                borderColor="border.default"
                overflowY="auto"
                py={2}
              >
                {conversations.length === 0 ? (
                  <Text fontSize="xs" color="text.secondary" px={3} py={2} opacity={0.5}>
                    No conversations yet
                  </Text>
                ) : (
                  conversations.map((conv) => (
                    <ConversationItem
                      key={conv.id}
                      conv={conv}
                      isActive={conv.id === activeConversationId}
                      onClick={() => handleSelectConversation(conv)}
                      onDelete={() => handleDeleteConversation(conv.id)}
                      onRename={(title) => handleRenameConversation(conv.id, title)}
                    />
                  ))
                )}
              </Box>

              {/* Main chat area */}
              <Flex flex={1} direction="column" overflow="hidden" position="relative">

              {/* Scroll navigation buttons — float over message area */}
              {activeMessages.length > 0 && (
                <HStack position="absolute" bottom="72px" right="12px" gap={1} zIndex={1}>
                  <IconButton
                    aria-label="Scroll to top"
                    size="xs"
                    variant="subtle"
                    onClick={scrollToTop}
                    title="Scroll to top"
                    opacity={0.7}
                    _hover={{ opacity: 1 }}
                  >
                    <LuArrowUp size={12} />
                  </IconButton>
                  <IconButton
                    aria-label="Scroll to bottom"
                    size="xs"
                    variant="subtle"
                    onClick={scrollToBottom}
                    title="Scroll to bottom"
                    opacity={0.7}
                    _hover={{ opacity: 1 }}
                  >
                    <LuArrowDown size={12} />
                  </IconButton>
                </HStack>
              )}

              {/* Message list */}
              <Drawer.Body
                ref={scrollAreaRef as any}
                flex={1}
                overflowY="auto"
                p={4}
                display="flex"
                flexDirection="column"
                gap={3}
              >
                {isLoadingConv ? (
                  <HStack justifyContent="center" mt={8}>
                    <Spinner size="sm" color="text.secondary" />
                  </HStack>
                ) : activeMessages.length === 0 ? (
                  <Stack gap={2} mt={8} px={4} opacity={0.6}>
                    <Text textAlign="center" color="text.secondary" fontSize="sm">
                      Try asking:
                    </Text>
                    {[
                      "Assess my pipeline",
                      "How does the headcount look for Anthropic?",
                      "What's in the pipeline right now?",
                      "Show me IRR data for Fund 1",
                    ].map((prompt) => (
                      <Box
                        key={prompt}
                        border="1px solid"
                        borderColor="border.default"
                        borderRadius="md"
                        px={3}
                        py={2}
                        cursor="pointer"
                        _hover={{ bg: "hover.bg" }}
                        onClick={() => setInput(prompt)}
                      >
                        <Text fontSize="xs" color="text.secondary">
                          {prompt}
                        </Text>
                      </Box>
                    ))}
                  </Stack>
                ) : null}

                {activeMessages.map((msg, i) => (
                  <Box
                    key={i}
                    alignSelf={msg.role === "user" ? "flex-end" : "flex-start"}
                    maxW={msg.role === "assistant" ? "98%" : "85%"}
                    bg={msg.role === "user" ? "blue.500" : "gray.700"}
                    color="white"
                    borderRadius="lg"
                    px={3}
                    py={2}
                    fontSize="sm"
                  >
                    {msg.role === "assistant" ? (
                      <Box>
                        {renderAssistantContent(msg.content, (text) => {
                          executePipelineRef.current = text
                          setInput(text)
                        })}
                        <CopyButton text={msg.content} />
                      </Box>
                    ) : (
                      <Box>
                        {messageAttachmentNames.current.has(i) && (
                          <HStack gap={1} mb={1} opacity={0.8}>
                            <LuPaperclip size={10} />
                            <Text fontSize="10px" lineClamp={1}>{messageAttachmentNames.current.get(i)}</Text>
                          </HStack>
                        )}
                        <Text whiteSpace="pre-wrap">{msg.content}</Text>
                      </Box>
                    )}
                  </Box>
                ))}

                {isLoading && (
                  <Box
                    alignSelf="flex-start"
                    maxW="98%"
                    bg="gray.700"
                    color="white"
                    borderRadius="lg"
                    px={3}
                    py={2}
                    fontSize="sm"
                  >
                    {streamingText ? (
                      renderMarkdown(streamingText)
                    ) : (
                      <HStack gap={2}>
                        <Spinner size="sm" color="whiteAlpha.600" />
                        <Text fontSize="xs" color="whiteAlpha.700">
                          {statusText || "Thinking..."}
                        </Text>
                      </HStack>
                    )}
                  </Box>
                )}

                <div ref={bottomRef} />
              </Drawer.Body>

              {/* Input area */}
              <Box
                borderTop="1px solid"
                borderColor="border.default"
                p={3}
                flexShrink={0}
              >
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,image/png,image/jpeg,image/gif,image/webp"
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                />

                {/* Attachment badge */}
                {attachment && (
                  <HStack
                    bg="blue.900"
                    borderRadius="md"
                    px={2}
                    py={1}
                    border="1px solid"
                    borderColor="blue.700"
                    mb={2}
                  >
                    <LuPaperclip size={11} color="#93c5fd" />
                    <Text fontSize="xs" color="blue.200" flex={1} lineClamp={1}>
                      {attachment.fileName}
                    </Text>
                    <Box
                      as="button"
                      onClick={handleRemoveAttachment}
                      color="blue.300"
                      _hover={{ color: "white" }}
                      lineHeight={1}
                    >
                      <LuX size={12} />
                    </Box>
                  </HStack>
                )}

                <HStack gap={2} align="flex-end">
                  {/* Paperclip button */}
                  <Box
                    as="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                    color={attachment ? "blue.400" : "text.secondary"}
                    _hover={{ color: "text.primary" }}
                    _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                    flexShrink={0}
                    lineHeight={1}
                    p={1}
                    title="Attach PDF or image"
                  >
                    <LuPaperclip size={16} />
                  </Box>
                  <Textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={activeTabId
                      ? `Ask about ${contextTabs.find(t => t.id === activeTabId)?.label ?? "this item"}...`
                      : "Ask about deals, companies, IRR..."
                    }
                    size="sm"
                    resize="none"
                    rows={2}
                    flex={1}
                    bg="page.bg"
                    borderColor="border.default"
                    color="text.primary"
                    _placeholder={{ color: "text.secondary" }}
                    disabled={isLoading}
                  />
                  {isLoading ? (
                    <Button
                      size="sm"
                      colorPalette="red"
                      onClick={handleStop}
                      flexShrink={0}
                      title="Stop"
                    >
                      <LuSquare />
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      colorPalette="blue"
                      onClick={handleSend}
                      disabled={!input.trim()}
                      flexShrink={0}
                    >
                      <LuSend />
                    </Button>
                  )}
                </HStack>
                <Text fontSize="xs" color="text.secondary" mt={1}>
                  Enter to send · Shift+Enter for new line · Attach PDF or image with{" "}
                  <Box as="span" display="inline-flex" verticalAlign="middle" mx="2px"><LuPaperclip size={10} /></Box>
                </Text>
              </Box>
              </Flex>  {/* end main chat Flex */}
            </HStack>  {/* end body row HStack */}

          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
